import { CTAButton } from "@/components/CTAButton";
import heroWebp480 from "@/assets/dr-ulisses-hero-480.webp.asset.json";
import heroWebp720 from "@/assets/dr-ulisses-hero-720.webp.asset.json";
import heroWebp960 from "@/assets/dr-ulisses-hero-960.webp.asset.json";
import heroJpg720 from "@/assets/dr-ulisses-hero-720.jpg.asset.json";
import { Video, Clock3, Users, Award, Sparkles, Receipt, ShieldAlert } from "lucide-react";

export function HeroSection() {
  return (
    <section className="relative overflow-hidden bg-[#F7F6F3] grain">
      {/* Orbs */}
      <div className="orb -top-40 -left-20 h-[520px] w-[520px] bg-[#0F2A47] opacity-[0.09]" aria-hidden="true" />
      <div className="orb top-1/3 -right-40 h-[440px] w-[440px] bg-[#17724F] opacity-[0.13]" aria-hidden="true" />
      <div className="orb -bottom-32 left-1/3 h-[360px] w-[360px] bg-[#C9A24C] opacity-[0.08]" aria-hidden="true" />
      <div className="grain-layer" aria-hidden="true" />

      {/* Hatch verde sutil */}
      <div className="absolute inset-0 hatch opacity-40 pointer-events-none" aria-hidden="true" />

      <div className="relative z-10 mx-auto max-w-[1180px] px-5 md:px-6 pt-6 pb-14 md:pt-[88px] md:pb-28">
        <div className="flex flex-col md:flex-row md:items-center md:gap-14">
          {/* Coluna texto */}
          <div className="md:w-[56%] order-1">
            {/* Badges */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-[#17724F]/20 bg-white/80 backdrop-blur px-3 py-1.5 shadow-[0_6px_24px_-8px_rgba(23,114,79,0.25)]">
                <span className="relative flex h-2 w-2 shrink-0">
                  <span className="pulse-dot absolute inline-flex h-full w-full rounded-full bg-[#17724F]" aria-hidden="true" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[#17724F]" />
                </span>
                <span className="text-[10.5px] md:text-[12px] font-semibold tracking-[0.02em] text-[#0F2A47] uppercase leading-tight">
                  Agenda limitada · 10 pacientes / semana
                </span>
              </div>

              <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-[#C9A24C]/35 bg-white/80 backdrop-blur px-3 py-1.5 shadow-[0_6px_24px_-8px_rgba(201,162,76,0.28)]">
                <Receipt size={12} strokeWidth={2} className="shrink-0 text-[#C9A24C]" aria-hidden="true" />
                <span className="text-[10.5px] md:text-[12px] font-semibold tracking-[0.02em] text-[#0F2A47] uppercase leading-tight">
                  Particular · a partir de R$ 600
                </span>
              </div>
            </div>

            <h1 className="mt-5 md:mt-6 font-display font-normal text-[30px] sm:text-[34px] md:text-[58px] leading-[1.08] md:leading-[1.04] text-[#0B1220] text-balance">
              Atendimento médico em saúde mental,{" "}
              <span className="relative inline-block">
                <span className="swash-green italic font-light">com tempo de verdade</span>
              </span>{" "}
              para escutar você
            </h1>

            <p className="mt-5 md:mt-6 text-[15.5px] md:text-[19px] text-[#4A4A4E] leading-[1.6] max-w-[580px]">
              Consulta particular por telemedicina, com plano de tratamento individual e retorno sempre com o mesmo médico. Agenda limitada a 10 pacientes por semana.
            </p>

            <div className="mt-5 max-w-[560px] rounded-xl border border-[#0F2A47]/10 bg-white/70 backdrop-blur px-4 py-3.5">
              <div className="flex items-start gap-2.5">
                <ShieldAlert size={15} strokeWidth={1.8} className="mt-[2px] shrink-0 text-[#0F2A47]/60" aria-hidden="true" />
                <p className="text-[12.5px] md:text-[13.5px] text-[#4A4A4E] leading-[1.6]">
                  <span className="font-semibold text-[#0B1220]">Antes de chamar no WhatsApp:</span> o atendimento é exclusivamente particular, a partir de R$ 600 por consulta, com pagamento antecipado. Não emito laudo, atestado ou receita avulsa, e não faço perícia. Documentos são emitidos apenas dentro do acompanhamento, quando há indicação clínica.
                </p>
              </div>
            </div>

            <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-4">
              <CTAButton eventName="form_open" size="xl" />
              <div className="hidden sm:flex items-center gap-2 text-[13px] text-[#6B6B70]">
                <Sparkles size={14} className="text-[#17724F]" aria-hidden="true" />
                <span>Resposta no mesmo dia · horários desta semana</span>
              </div>
            </div>

            <p className="mt-4 text-[12.5px] text-[#6B6B70]">
              Dr. Ulisses Contente, médico, CRM/SP 245805
            </p>

            {/* Trust cards */}
            <div className="mt-8 md:mt-10 grid grid-cols-2 md:grid-cols-4 gap-2.5 md:gap-3 max-w-[580px]">
              {[
                { Icon: Clock3, label: "Avaliação estendida" },
                { Icon: Users, label: "Mesmo médico" },
                { Icon: Video, label: "100% online" },
                { Icon: Award, label: "Pós em Psiquiatria" },
              ].map(({ Icon, label }) => (
                <div
                  key={label}
                  className="group relative rounded-xl border border-[#17724F]/12 bg-white/70 backdrop-blur px-2.5 md:px-3 py-2.5 flex items-center gap-2 min-w-0 transition-all hover:border-[#17724F]/40 hover:bg-white hover:-translate-y-0.5"
                >
                  <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-[#17724F]/12 to-[#17724F]/5 text-[#17724F] group-hover:from-[#17724F] group-hover:to-[#125C3F] group-hover:text-white transition-colors">
                    <Icon size={14} strokeWidth={1.9} aria-hidden="true" />
                  </div>
                  <span className="min-w-0 text-[11.5px] md:text-[12px] font-semibold text-[#0B1220] leading-tight">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Coluna foto */}
          <div className="mt-12 md:mt-0 md:w-[44%] order-2">
            <div className="relative mx-auto max-w-[360px] md:max-w-[420px]">
              {/* Anel pulsante emerald */}
              <div className="absolute -inset-3 md:-inset-4 rounded-[28px] border-2 border-[#17724F]/45 pulse-ring" aria-hidden="true" />
              {/* frame gold */}
              <div className="absolute -inset-2 md:-inset-3 rounded-[26px] bg-gradient-to-br from-[#C9A24C]/45 via-transparent to-[#17724F]/35 blur-md" aria-hidden="true" />
              <div className="absolute -inset-1 rounded-[22px] border border-white/60" aria-hidden="true" />

              <div
                className="relative w-full overflow-hidden rounded-[20px] bg-[#1A1A1D]"
                style={{ aspectRatio: "4 / 5" }}
              >
                <picture>
                  <source
                    type="image/webp"
                    srcSet={`${heroWebp480.url} 480w, ${heroWebp720.url} 720w, ${heroWebp960.url} 960w`}
                    sizes="(min-width: 768px) 420px, 360px"
                  />
                  <img
                    src={heroJpg720.url}
                    alt="Dr. Ulisses Contente, médico psiquiatra"
                    width={720}
                    height={900}
                    // Sem fetchPriority="high" aqui: no desktop a prioridade já vem
                    // do <link rel=preload media="(min-width:768px)"> da rota, e no
                    // mobile a foto não é o LCP — subir a prioridade dela só roubava
                    // banda das webfonts do H1.
                    decoding="async"
                    className="h-full w-full object-cover object-center"
                  />
                </picture>
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0B1220]/45 via-transparent to-transparent" aria-hidden="true" />
                {/* SVG assinatura na foto */}
                <svg className="absolute bottom-4 right-4 opacity-70" width="86" height="34" viewBox="0 0 86 34" fill="none" aria-hidden="true">
                  <path d="M4 24 C 10 8, 20 8, 22 20 C 24 32, 32 16, 40 18 C 48 20, 56 6, 66 20 C 72 28, 78 22, 82 18" stroke="#C9A24C" strokeWidth="1.2" strokeLinecap="round" fill="none" />
                </svg>
              </div>

              {/* Badge credencial */}
              <div className="floaty absolute left-1 md:-left-8 bottom-6 md:bottom-8 rounded-2xl bg-white/95 backdrop-blur px-3 md:px-4 py-2.5 md:py-3 shadow-[0_16px_40px_-10px_rgba(11,18,32,0.28)] border border-[#17724F]/12">
                <p className="text-[9.5px] md:text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[#17724F]">Credenciado</p>
                <p className="mt-0.5 text-[12px] md:text-[13px] font-semibold text-[#0B1220]">CRM/SP 245805</p>
                <p className="text-[10.5px] md:text-[11.5px] text-[#4A4A4E]">Pós em Psiquiatria</p>
              </div>

              {/* Badge telemedicina */}
              <div
                className="floaty absolute right-1 md:-right-6 top-4 md:top-6 rounded-2xl bg-[#0F2A47] text-white px-3 md:px-4 py-2.5 md:py-3 ring-soft border border-[#C9A24C]/20"
                style={{ animationDelay: "-2s" }}
              >
                <p className="text-[9.5px] md:text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[#C9A24C]">Telemedicina</p>
                <p className="mt-0.5 text-[12px] md:text-[13px] font-semibold">Todo o Brasil</p>
                <p className="text-[10.5px] md:text-[11.5px] text-white/70">Por videochamada</p>
              </div>

              {/* Chip live vagas */}
              <div className="absolute -bottom-3 md:-bottom-4 right-2 md:right-8 rounded-full bg-gradient-to-r from-[#17724F] to-[#125C3F] text-white px-3 md:px-4 py-1.5 md:py-2 shadow-[0_12px_30px_-10px_rgba(23,114,79,0.6)] flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="pulse-dot absolute inline-flex h-full w-full rounded-full bg-white" aria-hidden="true" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
                </span>
                <span className="text-[10.5px] md:text-[11.5px] font-semibold uppercase tracking-[0.12em]">Vagas esta semana</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Divisor onda */}
      <div className="relative z-10">
        <svg className="block w-full text-white" viewBox="0 0 1440 60" preserveAspectRatio="none" aria-hidden="true">
          <path d="M0,40 C240,10 480,60 720,40 C960,20 1200,50 1440,30 L1440,60 L0,60 Z" fill="currentColor" />
        </svg>
      </div>
    </section>
  );
}
